import { dateToString, now, FMTS, toISODatetimeUTC } from "./datetime"

const FORMATTERS = {
  csv: [dataToCsv, {}, { format: "data:text/plain;charset=utf-8," }]
}

/**
 * Generate CSV string from dataset Array
 * @param {*} data    The Array of object to export
 * @param {*} options The options to use to format data, { separator }
 * @returns CSV text string
 */
function dataToCsv(datas, options) {
  const { separator } = options
  if (datas.length < 1) {
    return ""
  }
  let final = []
  for (let datakey in datas) {
    // Get headers
    let data = datas[datakey]
    const headers = Object.keys(data[0])
    let result = headers.join(separator) + "\n"

    // Add the rows
    data.forEach(obj => {
      result +=
        headers
          .map(v => (typeof obj[v] == "object" ? convertArray(obj[v]) : String(obj[v])))
          .map(v => v.replaceAll('"', '""'))
          .map(v => `"${v}"`)
          .join(separator) + "\n"
    })
    final.push(result)
  }
  return final
}

/**
 * Convert an array to a string
 * @param {*} array      array to convert
 * @returns Array string
 */
function convertArray(array) {
  return JSON.stringify(array)
}

/**
 * Download data. Generate link to download data as file, emulate click.
 * @param {*} data      The data to download
 * @param {*} filename  The filename to use
 * @param {*} options   The dl options to use, { format } default to "text/plain"
 */
function download(data, filename, options) {
  let { format } = options
  format = format || "data:text/plain;charset=utf-8,"
  const dataURL = format + encodeURIComponent(data)
  downloadRaw(dataURL, filename)
}

/**
 * Download data. Generate link to download data as file, emulate click.
 * @param {*} dataURL   The raw data to download
 * @param {*} filename  The filename to use
 */
function downloadRaw(dataURL, filename) {
  let element = document.createElement("a")
  element.setAttribute("href", dataURL)
  element.setAttribute("download", filename)
  element.style.display = "none"
  element.click()
  // Memory managment / usage
  // See: https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL_static#usage_notes
  URL.revokeObjectURL(dataURL)
}

/**
 * Pre-format Non-Conformity data
 * @param {*} data  The Array of non-conformities to format
 * @returns         Pre-formated Array of object, ready to export
 */
function fmtNonConformityData(data) {
  return data.map(e => ({
    id: e.id,
    area: e.area,
    event: e.event,
    status: e.status,
    other: e.other,
    report_date: toISODatetimeUTC(e.report_date),
    quick_description: e.quick_description,
    detailed_description: e.detailed_description,
    root_cause_analysis: e.root_cause_analysis,
    due_to: e.due_to,
    created_by: e.created_by,
    updated_by: e.updated_by,
    created_at: toISODatetimeUTC(e.created_at),
    updated_at: toISODatetimeUTC(e.updated_at),
    closed_at: e.closed_at ? toISODatetimeUTC(e.closed_at) : "",
    inspection: e.inspection.name
  }))
}

/**
 * Pre-format Inspection data
 * @param {*} data  The Array of inspections to format
 * @returns         Pre-formated Array of object, ready to export
 */
function fmtInspectionData(data) {
  return data.map(e => ({
    id: e.id,
    created_at: toISODatetimeUTC(e.created_at),
    created_by: e.created_by,
    end_date: toISODatetimeUTC(e.end_date),
    name: e.name,
    project: e.project.name,
    start_date: toISODatetimeUTC(e.start_date),
    status: e.status,
    updated_at: toISODatetimeUTC(e.updated_at),
    updated_by: e.updated_by,
    uuid: e.uuid
  }))
}

/**
 * Pre-format Template data
 * @param {*} data  The Array of template values to format
 * @returns         Pre-formated Array of object, ready to export
 */
function fmtTemplate(data) {
  return [
    {
      id: data.id,
      company: data.company.name,
      created_at: toISODatetimeUTC(data.created_at),
      created_by: data.created_by,
      description: data.description,
      name: data.name,
      state: data.state.label,
      updated_at: toISODatetimeUTC(data.updated_at),
      updated_by: data.updated_by
    }
  ]
}

/**
 * Pre-format Values data
 * @param {*} data  The Array of Values to format
 * @returns         Pre-formated Array of object, ready to export
 */
function fmtValues(data) {
  const result = []
  data.sections.forEach(e => {
    const section = {
      section_title: e.title,
      section_description: e.description,
      section_position: e.position
    }
    e.fields.forEach(f => {
      const field = { ...section }
      field["field_name"] = f.name
      field["field_position"] = f.position
      field["field_type"] = f.type.name
      field["field_value"] = getValue(f)
      field["field_extra"] = getExtra(f)
      result.push(field)
    })
  })
  return result
}

function getValue(f) {
  switch (f.type.component.name) {
    case "ImageField":
      return f.values[0].fkey ? "Image uploaded" : "No image uploaded"
    case "Checkbox": {
      let checked = "Nothing is checked"
      let keysV = Object.keys(f.values[0])
      keysV.forEach(key => {
        if (key != "id" && f.values[0][key] == "1") {
          checked = key
          return
        }
      })
      return checked
    }
    case "Table":
      return f.values
    case "Selector":
      return f.values[0].selected
    case "DateField":
      return toISODatetimeUTC(f.values[0].value)
    default: // TextField, TextArea, Datefield, Table
      return f.values[0].value
  }
}

function getExtra(f) {
  switch (f.type.component.name) {
    case "ImageField":
      return f.values[0].fkey
        ? { name: f.values[0].name, description: f.values[0].description }
        : ""
    case "Selector":
      return { comment: f.values[0].comment }
    default: // TextField, TextArea, Datefield, Checkbox
      return ""
  }
}

/**
 * Filename generator method
 * @param {*} extension The file extension to use
 * @param {*} prefix Prefix to use to generate filename
 * @returns The filename string
 */
function getFilename(extension, prefix = null) {
  const timestamp = ~~(Date.now() / 1000)
  let filename = `${timestamp.toString(16)}.${extension || "txt"}`
  if (prefix) {
    filename = `${prefix}-${filename}`
  }

  return filename
}

/**
 * Handle export data to csv
 * @param {*} datas Datas to export
 */
function handleExportCSV(datas, format) {
  const date = dateToString(now(), FMTS.FILE)
  const filename = `${date}.${format}`
  const [fmt, fmtOptions, dlOptions] = FORMATTERS[format]
  download(fmt(datas, fmtOptions), filename, dlOptions)
}

export {
  dataToCsv,
  download,
  downloadRaw,
  fmtNonConformityData,
  fmtInspectionData,
  fmtTemplate,
  fmtValues,
  getFilename,
  handleExportCSV,
  FORMATTERS
}
